/**
 * <klaviyo-form-fallback> — a Klaviyo embed that degrades to a Shopify form.
 *
 * Klaviyo mounts its form into the child carrying the klaviyo-form-<id> class.
 * When nothing has been mounted by the timeout — script blocked, marketing
 * consent declined, form archived in Klaviyo, or its targeting rules hiding it
 * from this shopper — the Shopify customer form sitting next to it is revealed
 * instead. That form creates the customer record Klaviyo syncs from, so the
 * sign-up still lands in the same list.
 *
 * Klaviyo can mount after the timeout on a slow load. If it does, and the
 * shopper has not started on the fallback, the Klaviyo form takes over again.
 *
 * <klaviyo-form-fallback timeout="6000">
 *   <div class="klaviyo-form-MitZLs" data-klaviyo-embed></div>
 *   <div data-klaviyo-fallback hidden>…Shopify customer form…</div>
 * </klaviyo-form-fallback>
 */
;(function () {
  const selectors = {
    embed: '[data-klaviyo-embed]',
    fallback: '[data-klaviyo-fallback]',
    note: '.note'
  }

  const defaultTimeout = 6000

  class KlaviyoFormFallback extends HTMLElement {
    connectedCallback() {
      this.embed = this.querySelector(selectors.embed)
      this.fallback = this.querySelector(selectors.fallback)

      if (!this.embed || !this.fallback) return

      // A posted-back success or error note means the shopper is already using
      // the fallback. Keep it on screen rather than waiting on Klaviyo again.
      if (this.fallback.querySelector(selectors.note)) {
        this._showFallback()
        return
      }

      if (this._mounted()) return

      this.observer = new MutationObserver(
        function () {
          if (!this._mounted()) return
          if (this.embed.hidden) this._showEmbed()
          else this._settle()
        }.bind(this)
      )
      this.observer.observe(this.embed, { childList: true, subtree: true })

      this.timer = setTimeout(this._settle.bind(this), this.timeout)
    }

    disconnectedCallback() {
      this._stop()
    }

    get timeout() {
      const value = parseInt(this.getAttribute('timeout'), 10)
      return isNaN(value) ? defaultTimeout : value
    }

    // Klaviyo injects its own markup, so an empty embed means it never arrived.
    _mounted() {
      return this.embed.children.length > 0
    }

    _settle() {
      if (this.timer) clearTimeout(this.timer)
      if (this._mounted()) {
        this._stop()
        return
      }
      // Keep watching, so a late Klaviyo form can still take its place.
      this._showFallback()
    }

    _showFallback() {
      this.embed.hidden = true
      this.fallback.hidden = false
      this.dispatchEvent(new CustomEvent('klaviyo-form:fallback', { bubbles: true }))
      this.fallback.addEventListener('focusin', this._stop.bind(this), { once: true })
    }

    _showEmbed() {
      this._stop()
      this.fallback.hidden = true
      this.embed.hidden = false
    }

    _stop() {
      if (this.observer) this.observer.disconnect()
      if (this.timer) clearTimeout(this.timer)
    }
  }

  if (!customElements.get('klaviyo-form-fallback')) {
    customElements.define('klaviyo-form-fallback', KlaviyoFormFallback)
  }
})()
